import { ExpiringStockBenefitType, ExpiringStockPromoType } from '../models';

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const PROMO_TYPE_LABELS: Record<ExpiringStockPromoType, string> = {
    IMMEDIATE_DISCOUNT: 'Immediate Discount',
    COUPON_DISCOUNT: 'Coupon Discount',
};

export const BENEFIT_TYPE_LABELS: Record<ExpiringStockBenefitType, string> = {
    COMMON: 'Standard Discount',
    EXTENDED: 'Extended Discount',
    REDUCE_PRODUCT_AMOUNT: 'Reduce Product Price',
    GRADUATED_DISCOUNT: 'Graduated Discount',
};

export type PromoPattern = 'STANDARD' | 'BUY_X_GET_Y' | 'EXTENDED_ADVANCED';

export const PROMO_PATTERN_LABELS: Record<PromoPattern, string> = {
    STANDARD: 'Standard',
    BUY_X_GET_Y: 'Buy X Get Y',
    EXTENDED_ADVANCED: 'Extended (advanced)',
};

// ---------------------------------------------------------------------------
// Benefit — COMMON
// ---------------------------------------------------------------------------

export type CommonDiscountType = 'DISCOUNT' | 'FIXED_PRICE';
export type CommonUnitType = 'PERCENTAGE' | 'FIXED';
export type CommonApplianceTarget = 'PRODUCT' | 'PRODUCTS_OF_COMMODITY_GROUP' | 'PRODUCTS_WITH_TAG';
export type CommonApplianceType =
    | 'CHEAPEST_TARGET'
    | 'EACH_TARGET'
    | 'EQUAL_ON_EACH_TARGET'
    | 'MOST_EXPENSIVE_TARGET'
    | 'RECEIPT';

export interface CommonBenefitFormState {
    discountType: CommonDiscountType;
    unitType: CommonUnitType;
    value: string;
    applianceTarget: CommonApplianceTarget;
    applianceType: CommonApplianceType;
    maxIterations: string;
}

export const DEFAULT_COMMON_BENEFIT: CommonBenefitFormState = {
    discountType: 'DISCOUNT',
    unitType: 'PERCENTAGE',
    value: '10',
    applianceTarget: 'PRODUCT',
    applianceType: 'EACH_TARGET',
    maxIterations: '1',
};

// ---------------------------------------------------------------------------
// Benefit — REDUCE_PRODUCT_AMOUNT
// ---------------------------------------------------------------------------

export interface ReduceProductAmountFormState {
    reductionValue: string;
}

export const DEFAULT_REDUCE_PRODUCT_AMOUNT: ReduceProductAmountFormState = {
    reductionValue: '1',
};

// ---------------------------------------------------------------------------
// Benefit — GRADUATED_DISCOUNT
// ---------------------------------------------------------------------------

export interface GraduatedDiscountFormState {
    layerId: string;
    exclusive: boolean;
}

export const DEFAULT_GRADUATED_DISCOUNT: GraduatedDiscountFormState = {
    layerId: '',
    exclusive: false,
};

// ---------------------------------------------------------------------------
// Benefit — EXTENDED (full structure matching Korona #23/#24)
// ---------------------------------------------------------------------------

export type ExtendedOperator = 'EQUALS' | 'MINUS' | 'PERCENTAGE';
export type ExtendedReference = 'RECEIPT' | 'REMAINDER' | 'SELECTION' | 'SUB_SELECTION';
export type SelectionAmountType = 'AMOUNT' | 'ALL';
export type SelectionFilterType = 'PRODUCT' | 'NONE' | 'TAG' | 'COMMODITY_GROUP';
export type SelectionSort = 'IRRELEVANT' | 'CHEAPEST' | 'MOST_EXPENSIVE';

export interface SelectionAmountFormState {
    type: SelectionAmountType;
    value: string;
}

export interface SelectionFilterFormState {
    type: SelectionFilterType;
    not: boolean;
}

export interface PositionSelectionFormState {
    countAmount: SelectionAmountFormState;
    discountAmount: SelectionAmountFormState;
    filter: SelectionFilterFormState;
    sort: SelectionSort;
}

export interface DiscountCalculationFormState {
    operator: ExtendedOperator;
    operand: string;
    reference: ExtendedReference;
    sumReference: ExtendedReference;
    receiptDiscount: boolean;
    positionSelection: PositionSelectionFormState;
}

export interface ExtendedBenefitFormState {
    exclusive: boolean;
    positionSelection: PositionSelectionFormState;
    calculations: DiscountCalculationFormState[];
}

const DEFAULT_AMOUNT_ALL: SelectionAmountFormState = { type: 'ALL', value: '' };
const DEFAULT_AMOUNT_ONE: SelectionAmountFormState = { type: 'AMOUNT', value: '1' };

export const DEFAULT_POSITION_SELECTION: PositionSelectionFormState = {
    countAmount: { ...DEFAULT_AMOUNT_ONE },
    discountAmount: { ...DEFAULT_AMOUNT_ONE },
    filter: { type: 'PRODUCT', not: false },
    sort: 'IRRELEVANT',
};

export const DEFAULT_CALC_POSITION_SELECTION: PositionSelectionFormState = {
    countAmount: { ...DEFAULT_AMOUNT_ALL },
    discountAmount: { ...DEFAULT_AMOUNT_ALL },
    filter: { type: 'NONE', not: false },
    sort: 'IRRELEVANT',
};

export const DEFAULT_DISCOUNT_CALCULATION: DiscountCalculationFormState = {
    operator: 'PERCENTAGE',
    operand: '100',
    reference: 'SUB_SELECTION',
    sumReference: 'SUB_SELECTION',
    receiptDiscount: false,
    positionSelection: { ...DEFAULT_CALC_POSITION_SELECTION },
};

export const DEFAULT_EXTENDED_BENEFIT: ExtendedBenefitFormState = {
    exclusive: true,
    positionSelection: { ...DEFAULT_POSITION_SELECTION },
    calculations: [{ ...DEFAULT_DISCOUNT_CALCULATION }],
};

/** @deprecated Thin stub kept for call sites that only need operator/operand. Prefer ExtendedBenefitFormState. */
export interface LegacyExtendedBenefitFormState {
    operator: ExtendedOperator;
    operand: string;
    reference: ExtendedReference;
    receiptDiscount: boolean;
}

// ---------------------------------------------------------------------------
// Buy X Get Y preset
// ---------------------------------------------------------------------------

export interface BuyXGetYFormState {
    buyQty: string;
    getQty: string;
    discountPercent: string;
    exclusive: boolean;
}

export const DEFAULT_BUY_X_GET_Y: BuyXGetYFormState = {
    buyQty: '1',
    getQty: '1',
    discountPercent: '100',
    exclusive: true,
};

export const BUY_X_GET_Y_PRESETS: { id: string; label: string; state: BuyXGetYFormState }[] = [
    { id: 'B1G1', label: 'Buy 1 Get 1 (free)', state: { buyQty: '1', getQty: '1', discountPercent: '100', exclusive: true } },
    { id: 'B2G1', label: 'Buy 2 Get 1 (free)', state: { buyQty: '2', getQty: '1', discountPercent: '100', exclusive: true } },
    { id: 'B1G20', label: 'Buy 1 Get 20% off', state: { buyQty: '1', getQty: '1', discountPercent: '20', exclusive: true } },
];

/**
 * Korona selection count:
 * - Free extras (discountPercent >= 100): countAmount = buyQty + getQty (e.g. B2G1 → 3)
 * - Partial % off (#24): countAmount = buyQty, discountAmount = getQty
 */
export function resolveBuyXGetYCounts(form: BuyXGetYFormState): {
    countAmount: number;
    discountAmount: number;
    triggerQty: number;
    discountPercent: number;
} {
    const buyQty = Math.max(1, parseInt(form.buyQty, 10) || 1);
    const getQty = Math.max(1, parseInt(form.getQty, 10) || 1);
    const discountPercent = Math.max(0, parseFloat(form.discountPercent) || 0);
    const isFree = discountPercent >= 100;
    const countAmount = isFree ? buyQty + getQty : buyQty;
    const discountAmount = getQty;
    return { countAmount, discountAmount, triggerQty: countAmount, discountPercent };
}

export function buildBuyXGetYNameTemplate(form: BuyXGetYFormState): string {
    const buyQty = Math.max(1, parseInt(form.buyQty, 10) || 1);
    const getQty = Math.max(1, parseInt(form.getQty, 10) || 1);
    const discountPercent = Math.max(0, parseFloat(form.discountPercent) || 0);
    if (discountPercent >= 100) {
        return `{{ product.name }} - Buy ${buyQty} Get ${getQty}, exp {{ tracker.product_expiry_date }}`;
    }
    return `{{ product.name }} - Buy ${buyQty} Get ${discountPercent}% off, exp {{ tracker.product_expiry_date }}`;
}

function buildAmountPayload(amount: SelectionAmountFormState): Record<string, unknown> {
    if (amount.type === 'ALL') {
        return { type: 'ALL' };
    }
    return { type: 'AMOUNT', value: parseFloat(amount.value) || 0 };
}

function buildFilterPayload(filter: SelectionFilterFormState): Record<string, unknown> {
    const payload: Record<string, unknown> = { type: filter.type, not: filter.not };
    if (filter.type === 'PRODUCT') {
        payload.products = [];
    }
    return payload;
}

function buildPositionSelectionPayload(selection: PositionSelectionFormState): Record<string, unknown> {
    return {
        countAmount: buildAmountPayload(selection.countAmount),
        discountAmount: buildAmountPayload(selection.discountAmount),
        filter: buildFilterPayload(selection.filter),
        sort: selection.sort,
    };
}

export function buildBuyXGetYBenefitConfig(form: BuyXGetYFormState): Record<string, unknown> {
    const { countAmount, discountAmount, discountPercent } = resolveBuyXGetYCounts(form);
    return {
        layer: { exclusive: form.exclusive },
        extended: {
            positionSelectionCriteria: {
                selections: [
                    {
                        countAmount: { type: 'AMOUNT', value: countAmount },
                        discountAmount: { type: 'AMOUNT', value: discountAmount },
                        filter: { type: 'PRODUCT', products: [], not: false },
                        sort: 'IRRELEVANT',
                    },
                ],
            },
            discountCalculations: [
                {
                    operator: 'PERCENTAGE',
                    operand: discountPercent,
                    reference: 'SUB_SELECTION',
                    sumReference: 'SUB_SELECTION',
                    receiptDiscount: false,
                    positionSelectionCriteria: {
                        selections: [
                            {
                                countAmount: { type: 'ALL' },
                                discountAmount: { type: 'ALL' },
                                filter: { type: 'NONE', not: false },
                                sort: 'IRRELEVANT',
                            },
                        ],
                    },
                },
            ],
        },
    };
}

export function buildBuyXGetYTriggerConfig(form: BuyXGetYFormState): Record<string, unknown> {
    const { triggerQty } = resolveBuyXGetYCounts(form);
    const condition = {
        value: triggerQty,
        equalityCondition: 'GREATER_OR_EQUAL' as EqualityCondition,
    };
    return {
        productAmount: { ...condition },
        receiptProductAmount: { ...condition },
        attachExternalCoupon: true,
    };
}

export function parseBuyXGetYFromConfigs(
    benefitConfig: Record<string, unknown>,
    triggerConfig: Record<string, unknown>
): BuyXGetYFormState | null {
    const extended = asRecord(benefitConfig.extended);
    if (!extended) {
        return null;
    }
    const criteria = asRecord(extended.positionSelectionCriteria);
    const selections = criteria?.selections;
    if (!Array.isArray(selections) || selections.length === 0) {
        return null;
    }
    const first = asRecord(selections[0]);
    if (!first) {
        return null;
    }
    const countAmount = asRecord(first.countAmount);
    const discountAmount = asRecord(first.discountAmount);
    const countValue = Number(countAmount?.value ?? 0);
    const discountValue = Number(discountAmount?.value ?? 1);
    const calculations = extended.discountCalculations;
    const firstCalc = Array.isArray(calculations) && calculations.length > 0 ? asRecord(calculations[0]) : null;
    const discountPercent = Number(firstCalc?.operand ?? 100);
    const layer = asRecord(benefitConfig.layer);
    const exclusive = layer?.exclusive !== false;

    const isFree = discountPercent >= 100;
    const buyQty = isFree ? Math.max(1, countValue - discountValue) : Math.max(1, countValue);
    const getQty = Math.max(1, discountValue);

    const productAmount = asRecord(triggerConfig.productAmount);
    const receiptAmount = asRecord(triggerConfig.receiptProductAmount);
    if (!productAmount || !receiptAmount) {
        // Still accept as Buy X Get Y if benefit shape matches.
    }

    return {
        buyQty: String(buyQty),
        getQty: String(getQty),
        discountPercent: String(discountPercent),
        exclusive,
    };
}

export function detectPromoPattern(
    benefitType: ExpiringStockBenefitType,
    benefitConfig: Record<string, unknown>,
    triggerConfig: Record<string, unknown>
): PromoPattern {
    if (benefitType !== 'EXTENDED') {
        return 'STANDARD';
    }
    const buyXGetY = parseBuyXGetYFromConfigs(benefitConfig, triggerConfig);
    if (buyXGetY && asRecord(triggerConfig.productAmount) && asRecord(triggerConfig.receiptProductAmount)) {
        const criteria = asRecord(asRecord(benefitConfig.extended)?.positionSelectionCriteria);
        const selections = criteria?.selections;
        if (Array.isArray(selections) && selections.length === 1) {
            const filter = asRecord(asRecord(selections[0])?.filter);
            if (filter?.type === 'PRODUCT') {
                return 'BUY_X_GET_Y';
            }
        }
    }
    return 'EXTENDED_ADVANCED';
}

// ---------------------------------------------------------------------------
// Triggers
// ---------------------------------------------------------------------------

export type TriggerType =
    | 'productAmount'
    | 'receiptProductAmount'
    | 'productAndReceiptAmount'
    | 'receiptRevenue'
    | 'tagAmount'
    | 'tagRevenue'
    | 'customerGroups'
    | 'customers';

export type EqualityCondition = 'EQUAL' | 'GREATER_OR_EQUAL';

export const TRIGGER_TYPE_LABELS: Record<TriggerType, string> = {
    productAmount: 'Product quantity',
    receiptProductAmount: 'Receipt product quantity',
    productAndReceiptAmount: 'Product + receipt quantity',
    receiptRevenue: 'Receipt revenue',
    tagAmount: 'Tag amount',
    tagRevenue: 'Tag revenue',
    customerGroups: 'Customer groups',
    customers: 'Specific customers',
};

export const EQUALITY_CONDITION_LABELS: Record<EqualityCondition, string> = {
    EQUAL: 'Exactly (=)',
    GREATER_OR_EQUAL: 'At least (≥)',
};

export interface AmountTriggerFormState {
    triggerType: TriggerType;
    value: string;
    equalityCondition: EqualityCondition;
    productId: string;
    tagId: string;
    /**
     * Always true for promo templates: an external coupon code is generated and
     * attached as `triggers[].externalCouponNumber` alongside this trigger's
     * condition at promo-creation time.
     */
    attachExternalCoupon: boolean;
    customerGroupIds: string;
    customerIds: string;
}

export const DEFAULT_TRIGGER: AmountTriggerFormState = {
    triggerType: 'productAmount',
    value: '1',
    equalityCondition: 'GREATER_OR_EQUAL',
    productId: '',
    tagId: '',
    attachExternalCoupon: true,
    customerGroupIds: '',
    customerIds: '',
};

// ---------------------------------------------------------------------------
// Build benefit_config JSON from form state
// ---------------------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

export function buildExtendedBenefitConfig(extended: ExtendedBenefitFormState): Record<string, unknown> {
    return {
        layer: { exclusive: extended.exclusive },
        extended: {
            positionSelectionCriteria: {
                selections: [buildPositionSelectionPayload(extended.positionSelection)],
            },
            discountCalculations: extended.calculations.map((calc) => ({
                operator: calc.operator,
                operand: parseFloat(calc.operand) || 0,
                reference: calc.reference,
                sumReference: calc.sumReference,
                receiptDiscount: calc.receiptDiscount,
                positionSelectionCriteria: {
                    selections: [buildPositionSelectionPayload(calc.positionSelection)],
                },
            })),
        },
    };
}

export function buildBenefitConfig(
    benefitType: ExpiringStockBenefitType,
    common: CommonBenefitFormState,
    reduceProductAmount: ReduceProductAmountFormState,
    graduated: GraduatedDiscountFormState,
    extended: ExtendedBenefitFormState
): Record<string, unknown> {
    switch (benefitType) {
        case 'COMMON': {
            const config: Record<string, unknown> = {
                type: common.discountType,
                unitType: common.unitType,
                value: parseFloat(common.value) || 0,
                applianceTarget: common.applianceTarget,
                applianceType: common.applianceType,
            };
            if (common.maxIterations) {
                config.maxIterations = parseInt(common.maxIterations, 10) || 1;
            }
            return { common: config };
        }
        case 'REDUCE_PRODUCT_AMOUNT':
            return {
                reduceProductAmount: {
                    reductionValue: parseFloat(reduceProductAmount.reductionValue) || 0,
                },
            };
        case 'GRADUATED_DISCOUNT':
            return {
                layer: {
                    exclusive: graduated.exclusive,
                    ...(graduated.layerId ? { layer: { id: graduated.layerId } } : {}),
                },
            };
        case 'EXTENDED':
            return buildExtendedBenefitConfig(extended);
        default:
            return {};
    }
}

// ---------------------------------------------------------------------------
// Parse benefit_config JSON into form state
// ---------------------------------------------------------------------------

export function parseCommonBenefit(config: Record<string, unknown>): CommonBenefitFormState {
    const common = asRecord(config.common) || config;
    return {
        discountType: (common.type as CommonDiscountType) || DEFAULT_COMMON_BENEFIT.discountType,
        unitType: (common.unitType as CommonUnitType) || DEFAULT_COMMON_BENEFIT.unitType,
        value: String(common.value ?? common.discountPercent ?? DEFAULT_COMMON_BENEFIT.value),
        applianceTarget: (common.applianceTarget as CommonApplianceTarget) || DEFAULT_COMMON_BENEFIT.applianceTarget,
        applianceType: (common.applianceType as CommonApplianceType) || DEFAULT_COMMON_BENEFIT.applianceType,
        maxIterations: String(common.maxIterations ?? DEFAULT_COMMON_BENEFIT.maxIterations),
    };
}

export function parseReduceProductAmount(config: Record<string, unknown>): ReduceProductAmountFormState {
    const reduce = asRecord(config.reduceProductAmount) || config;
    return {
        reductionValue: String(reduce.reductionValue ?? reduce.value ?? DEFAULT_REDUCE_PRODUCT_AMOUNT.reductionValue),
    };
}

export function parseGraduatedDiscount(config: Record<string, unknown>): GraduatedDiscountFormState {
    const layer = asRecord(config.layer) || config;
    const layerRef = asRecord(layer.layer);
    return {
        layerId: String(layerRef?.id ?? ''),
        exclusive: Boolean(layer.exclusive),
    };
}

function parseSelectionAmount(raw: unknown, fallback: SelectionAmountFormState): SelectionAmountFormState {
    const amount = asRecord(raw);
    if (!amount) {
        return { ...fallback };
    }
    if (amount.type === 'ALL') {
        return { type: 'ALL', value: '' };
    }
    return { type: 'AMOUNT', value: String(amount.value ?? fallback.value) };
}

function parsePositionSelection(raw: unknown, fallback: PositionSelectionFormState): PositionSelectionFormState {
    const selection = asRecord(raw);
    if (!selection) {
        return {
            countAmount: { ...fallback.countAmount },
            discountAmount: { ...fallback.discountAmount },
            filter: { ...fallback.filter },
            sort: fallback.sort,
        };
    }
    const filter = asRecord(selection.filter) || {};
    return {
        countAmount: parseSelectionAmount(selection.countAmount, fallback.countAmount),
        discountAmount: parseSelectionAmount(selection.discountAmount, fallback.discountAmount),
        filter: {
            type: (filter.type as SelectionFilterType) || fallback.filter.type,
            not: Boolean(filter.not),
        },
        sort: (selection.sort as SelectionSort) || fallback.sort,
    };
}

export function parseExtendedBenefit(config: Record<string, unknown>): ExtendedBenefitFormState {
    const extended = asRecord(config.extended) || config;
    const layer = asRecord(config.layer);
    const criteria = asRecord(extended.positionSelectionCriteria);
    const selections = Array.isArray(criteria?.selections) ? criteria.selections : [];
    const positionSelection = parsePositionSelection(selections[0], DEFAULT_POSITION_SELECTION);

    const calculationsRaw = Array.isArray(extended.discountCalculations) ? extended.discountCalculations : [];
    const calculations: DiscountCalculationFormState[] =
        calculationsRaw.length > 0
            ? calculationsRaw.map((raw) => {
                  const calc = asRecord(raw) || {};
                  const calcCriteria = asRecord(calc.positionSelectionCriteria);
                  const calcSelections = Array.isArray(calcCriteria?.selections) ? calcCriteria.selections : [];
                  return {
                      operator: (calc.operator as ExtendedOperator) || DEFAULT_DISCOUNT_CALCULATION.operator,
                      operand: String(calc.operand ?? DEFAULT_DISCOUNT_CALCULATION.operand),
                      reference: (calc.reference as ExtendedReference) || DEFAULT_DISCOUNT_CALCULATION.reference,
                      sumReference:
                          (calc.sumReference as ExtendedReference) ||
                          (calc.reference as ExtendedReference) ||
                          DEFAULT_DISCOUNT_CALCULATION.sumReference,
                      receiptDiscount: Boolean(calc.receiptDiscount),
                      positionSelection: parsePositionSelection(calcSelections[0], DEFAULT_CALC_POSITION_SELECTION),
                  };
              })
            : [{ ...DEFAULT_DISCOUNT_CALCULATION }];

    return {
        exclusive: layer?.exclusive !== undefined ? Boolean(layer.exclusive) : true,
        positionSelection,
        calculations,
    };
}

export function extendedFromBuyXGetY(form: BuyXGetYFormState): ExtendedBenefitFormState {
    const { countAmount, discountAmount, discountPercent } = resolveBuyXGetYCounts(form);
    return {
        exclusive: form.exclusive,
        positionSelection: {
            countAmount: { type: 'AMOUNT', value: String(countAmount) },
            discountAmount: { type: 'AMOUNT', value: String(discountAmount) },
            filter: { type: 'PRODUCT', not: false },
            sort: 'IRRELEVANT',
        },
        calculations: [
            {
                operator: 'PERCENTAGE',
                operand: String(discountPercent),
                reference: 'SUB_SELECTION',
                sumReference: 'SUB_SELECTION',
                receiptDiscount: false,
                positionSelection: { ...DEFAULT_CALC_POSITION_SELECTION },
            },
        ],
    };
}

export function triggerFromBuyXGetY(form: BuyXGetYFormState): AmountTriggerFormState {
    const { triggerQty } = resolveBuyXGetYCounts(form);
    return {
        ...DEFAULT_TRIGGER,
        triggerType: 'productAndReceiptAmount',
        value: String(triggerQty),
        equalityCondition: 'GREATER_OR_EQUAL',
        productId: '',
        attachExternalCoupon: true,
    };
}

// ---------------------------------------------------------------------------
// Build trigger_config JSON from form state
// ---------------------------------------------------------------------------

function parseIdList(csv: string): { id: string }[] {
    return csv
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((id) => ({ id }));
}

function buildBaseTriggerConfig(form: AmountTriggerFormState): Record<string, unknown> {
    const { triggerType } = form;
    const value = parseFloat(form.value) || 0;

    switch (triggerType) {
        case 'productAmount': {
            const condition: Record<string, unknown> = {
                value,
                equalityCondition: form.equalityCondition,
            };
            if (form.productId) {
                condition.product = { id: form.productId };
            }
            return { productAmount: condition };
        }
        case 'receiptProductAmount':
            return {
                receiptProductAmount: {
                    value,
                    equalityCondition: form.equalityCondition,
                },
            };
        case 'productAndReceiptAmount': {
            const condition: Record<string, unknown> = {
                value,
                equalityCondition: form.equalityCondition,
            };
            const productCondition = { ...condition };
            if (form.productId) {
                productCondition.product = { id: form.productId };
            }
            return {
                productAmount: productCondition,
                receiptProductAmount: condition,
            };
        }
        case 'receiptRevenue':
            return {
                receiptRevenue: {
                    value,
                    equalityCondition: form.equalityCondition,
                },
            };
        case 'tagAmount':
            return {
                tagAmount: {
                    value,
                    equalityCondition: form.equalityCondition,
                    ...(form.tagId ? { tag: { id: form.tagId } } : {}),
                },
            };
        case 'tagRevenue':
            return {
                tagRevenue: {
                    value,
                    equalityCondition: form.equalityCondition,
                    ...(form.tagId ? { tag: { id: form.tagId } } : {}),
                },
            };
        case 'customerGroups':
            return { customerGroups: parseIdList(form.customerGroupIds) };
        case 'customers':
            return { customers: parseIdList(form.customerIds) };
        default:
            return {};
    }
}

/**
 * Builds `trigger_config` from the trigger-type condition plus a mandatory
 * `attachExternalCoupon` flag - a sibling of the condition (never a replacement for
 * it). The backend resolves that flag into a generated `externalCouponNumber` on
 * the trigger at promo-creation time.
 */
export function buildTriggerConfig(form: AmountTriggerFormState): Record<string, unknown> {
    const config = buildBaseTriggerConfig(form);
    config.attachExternalCoupon = true;
    return config;
}

// ---------------------------------------------------------------------------
// Parse trigger_config JSON into form state (supports legacy amount/comparator keys)
// ---------------------------------------------------------------------------

function parseEqualityCondition(raw: unknown): EqualityCondition {
    if (raw === 'EQUAL' || raw === 'GREATER_OR_EQUAL') {
        return raw;
    }
    return DEFAULT_TRIGGER.equalityCondition;
}

function parseNumericValue(condition: Record<string, unknown>): string {
    const value = condition.value ?? condition.amount;
    return String(value ?? DEFAULT_TRIGGER.value);
}

function parseModelRefId(ref: unknown): string {
    const record = asRecord(ref);
    return record?.id ? String(record.id) : '';
}

/** Keys that ride alongside a trigger condition rather than naming one. */
const NON_TRIGGER_TYPE_KEYS = ['attachExternalCoupon', 'externalCouponNumber'];

export function parseTriggerConfig(config: Record<string, unknown>): AmountTriggerFormState {
    if (!config || Object.keys(config).length === 0) {
        return { ...DEFAULT_TRIGGER };
    }

    const attachExternalCoupon = true;
    const triggerTypeKeys = Object.keys(config).filter((key) => !NON_TRIGGER_TYPE_KEYS.includes(key));

    const hasProductAmount = !!asRecord(config.productAmount);
    const hasReceiptProductAmount = !!asRecord(config.receiptProductAmount);

    if (hasProductAmount && hasReceiptProductAmount) {
        const condition = asRecord(config.productAmount) || {};
        return {
            triggerType: 'productAndReceiptAmount',
            value: parseNumericValue(condition),
            equalityCondition: parseEqualityCondition(condition.equalityCondition ?? condition.comparator),
            productId: parseModelRefId(condition.product),
            tagId: '',
            attachExternalCoupon,
            customerGroupIds: '',
            customerIds: '',
        };
    }

    const triggerType = (triggerTypeKeys[0] as TriggerType) || 'productAmount';
    const condition = asRecord(config[triggerType]) || {};

    const base: AmountTriggerFormState = {
        triggerType,
        value: parseNumericValue(condition),
        equalityCondition: parseEqualityCondition(condition.equalityCondition ?? condition.comparator),
        productId: parseModelRefId(condition.product),
        tagId: parseModelRefId(condition.tag),
        attachExternalCoupon,
        customerGroupIds: '',
        customerIds: '',
    };

    if (triggerType === 'customerGroups' && Array.isArray(config.customerGroups)) {
        base.customerGroupIds = config.customerGroups
            .map((g) => asRecord(g)?.id)
            .filter(Boolean)
            .join(', ');
    }
    if (triggerType === 'customers' && Array.isArray(config.customers)) {
        base.customerIds = config.customers
            .map((c) => asRecord(c)?.id)
            .filter(Boolean)
            .join(', ');
    }

    return base;
}

export const DEFAULT_PROMO_NAME_TEMPLATE =
    '{{ product.name }} - {{ discount_percent }}% off, exp {{ tracker.product_expiry_date }}';
